import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities, deleteEntity } from '@redux/entities'

import api from '@functions/api'

import type { BusinessUnitModel } from '@models/businessUnit'
import type { AppDispatch } from '@redux/store'
import type {
  Action, ActionResult, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/businessUnit/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/businessUnit/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/businessUnit/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/businessUnit/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/businessUnit/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/businessUnit/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/businessUnit/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/businessUnit/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/businessUnit/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/businessUnit/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/businessUnit/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/businessUnit/DELETE_FAILURE'

export type BusinessUnitModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

const initialState: BusinessUnitModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}): Action {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess(): Action {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []): Action {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(): Action {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(): Action {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []): Action {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(): Action {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(): Action {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []): Action {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(): Action {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(): Action {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []): Action {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadBusinessUnits(options: EntityOptions = {}) {
  return (dispatch: AppDispatch): Promise<ActionResult<BusinessUnitModel[]>> => {
    dispatch(fetchRequest())

    const promise = api('/business-units', options)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.BUSINESS_UNIT_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))
        return { success: false, result: errors }
      })

    return promise
  }
}

export function loadBusinessUnit(businessUnitId: number, options: EntityOptions = {}) {
  return (dispatch: AppDispatch, getState: () => RootReducerState): Promise<ActionResult<BusinessUnitModel>> => {
    const state = getState()
    const loadedIds = [...state.businessUnits.loadedIds]

    if (businessUnitId && !loadedIds.includes(businessUnitId)){
      loadedIds.push(businessUnitId)
    }

    dispatch(fetchRequest({ loadedIds }))

    const promise = api(`/business-units/${businessUnitId}`, options)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.BUSINESS_UNIT)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())
        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))
        return { success: false, result: errors }
      })

    return promise
  }
}

export function createBusinessUnit(businessUnit: BusinessUnitModel, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify(businessUnit),
  }

  return (dispatch: AppDispatch): Promise<ActionResult<BusinessUnitModel>> => {
    dispatch(createRequest())

    const promise = api('/business-units', options, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.BUSINESS_UNIT)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function updateBusinessUnit(businessUnit: BusinessUnitModel, options: EntityOptions) {
  const config = {
    method: 'PUT',
    data: JSON.stringify(businessUnit),
  }

  return (dispatch: AppDispatch): Promise<ActionResult<BusinessUnitModel>> => {
    dispatch(updateRequest())

    const promise = api(`/business-units/${businessUnit.id}`, options, config)
      .then(({ data }: { data: BusinessUnitModel }) => {
        const normalizedJson = normalize(data, Schemas.BUSINESS_UNIT)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function deleteBusinessUnit(businessUnit: BusinessUnitModel) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch): Promise<ActionResult<BusinessUnitModel>> => {
    dispatch(deleteRequest())

    const promise = api(`/business-units/${businessUnit.id}`, {}, config)
      .then(({ data }) => {
        const normalizedJson = normalize(data, Schemas.BUSINESS_UNIT)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false, errors }
      })
    return promise
  }
}

// Reducer
export default function reducer(
  state: BusinessUnitModuleState = initialState,
  action: Action = { type: '' },
): BusinessUnitModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
